import React, { useState } from 'react';
import { X, User, Building, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';

const ProfileCompletionModal = ({ isOpen, onClose, onComplete, user }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const steps = [
    {
      id: 1,
      title: "What's your first name?",
      field: 'firstName',
      placeholder: 'Enter your first name',
      icon: User,
      required: true
    },
    {
      id: 2,
      title: "What's your last name?",
      field: 'lastName',
      placeholder: 'Enter your last name',
      icon: User,
      required: true
    },
    {
      id: 3,
      title: "What is the name of your company?",
      field: 'company',
      placeholder: 'Enter your company name',
      icon: Building,
      required: false
    }
  ];

  const currentStepData = steps.find(step => step.id === currentStep);

  const handleInputChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      [currentStepData.field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[currentStepData.field]) {
      setErrors(prev => ({
        ...prev,
        [currentStepData.field]: ''
      }));
    }
  };

  const validateCurrentStep = () => {
    const newErrors = {};
    const value = formData[currentStepData.field];
    
    if (currentStepData.required && !value.trim()) {
      newErrors[currentStepData.field] = `${currentStepData.title.replace('?', '')} is required`;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      if (currentStep < steps.length) {
        setCurrentStep(currentStep + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/auth/complete-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          company: formData.company,
          phone: null,
          position: null
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        onComplete(data.user);
        onClose();
      } else {
        setErrors({ submit: data.message || 'Failed to update profile' });
      }
    } catch (error) {
      console.error('Profile completion error:', error);
      setErrors({ submit: 'Network error. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const IconComponent = currentStepData.icon;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md bg-white rounded-lg shadow-xl">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <IconComponent className="h-5 w-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">
                Complete Your Profile
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-6">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep > step.id 
                    ? 'bg-green-500 text-white' 
                    : currentStep === step.id 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {currentStep > step.id ? <Check className="h-4 w-4" /> : step.id}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-8 h-0.5 mx-2 ${
                    currentStep > step.id ? 'bg-green-500' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>

          {/* Step Content */}
          <div className="text-center mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {currentStepData.title}
            </h3>
            <p className="text-sm text-gray-600">
              Step {currentStep} of {steps.length}
            </p>
          </div>

          {/* Input Field */}
          <div className="mb-6">
            <div className="relative">
              <IconComponent className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                value={formData[currentStepData.field]}
                onChange={handleInputChange}
                className={`pl-10 ${errors[currentStepData.field] ? 'border-red-500' : ''}`}
                placeholder={currentStepData.placeholder}
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleNext();
                  }
                }}
              />
            </div>
            {errors[currentStepData.field] && (
              <p className="mt-2 text-sm text-red-600">{errors[currentStepData.field]}</p>
            )}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md mb-4">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex space-x-3">
            {currentStep > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={handlePrevious}
                className="flex-1"
                disabled={isSubmitting}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
            
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isSubmitting}
            >
              Skip for Now
            </Button>
            
            <Button
              type="button"
              onClick={handleNext}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : currentStep === steps.length ? 'Complete' : 'Next'}
              {currentStep < steps.length && <ArrowRight className="h-4 w-4 ml-2" />}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ProfileCompletionModal;
